import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { CheckCircle2, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("polling");
  const [kind, setKind] = useState("entry");
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      navigate("/lobby", { replace: true });
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      while (attempts < 15 && !cancelled) {
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.type) setKind(data.type);
          if (data.payment_status === "paid") {
            if (!cancelled) {
              setStatus("paid");
              await refreshUser();
            }
            return;
          }
          if (data.payment_status === "failed" || data.payment_status === "expired") {
            if (!cancelled) setStatus("failed");
            return;
          }
        } catch (err) {
          console.warn("Payment status poll failed, retrying", err);
        }
        attempts += 1;
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshUser, navigate]);

  const COPY = {
    deposit: {
      eyebrow: "Funds Added",
      heading: "WALLET TOPPED UP",
      body: "Your balance is live. Pick a slate and enter straight from your wallet.",
      primary: { label: "Enter a Contest", to: "/lobby" },
      secondary: { label: "My Wallet", to: "/wallet" },
    },
    coach_pass: {
      eyebrow: "Coach Pass Active",
      heading: "UNLIMITED AI READS",
      body: "Every slate is unlocked for the length of your pass. Go read the board.",
      primary: { label: "Back to Lobby", to: "/lobby" },
      secondary: { label: "My Dashboard", to: "/dashboard" },
    },
    subscription: {
      eyebrow: "Coach Pro Active",
      heading: "YOU'RE SUBSCRIBED",
      body: "Unlimited AI reads every week. Manage or cancel any time from your dashboard.",
      primary: { label: "Back to Lobby", to: "/lobby" },
      secondary: { label: "Manage Plan", to: "/dashboard" },
    },
    entry: {
      eyebrow: "Entry Confirmed",
      heading: "YOU'RE IN THE GAME",
      body: "Your picks are locked. Track your position on the leaderboard after game time.",
      primary: { label: "Enter Another", to: "/lobby" },
      secondary: { label: "My Entries", to: "/entries" },
    },
  };
  const copy = COPY[kind] || COPY.entry;

  return (
    <Layout>
      <div className="max-w-xl mx-auto text-center py-16">
        {status === "polling" && (
          <>
            <div className="font-display text-4xl tracking-widest animate-pulse mb-4">CONFIRMING...</div>
            <p className="text-white/50 text-sm">Checking your payment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#00FF66] flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={32} className="text-black" />
            </div>
            <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-2"
              data-testid="success-eyebrow">{copy.eyebrow}</div>
            <h1 className="font-display text-4xl sm:text-5xl mb-4">{copy.heading}</h1>
            <p className="text-white/50 mb-8">{copy.body}</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={() => navigate(copy.secondary.to)}
                data-testid="success-view-entries"
                className="border border-white/20 hover:border-white/40 text-white font-bold uppercase tracking-widest px-5 py-2.5 text-sm rounded-sm"
              >
                {copy.secondary.label}
              </button>
              <button
                onClick={() => navigate(copy.primary.to)}
                data-testid="success-back-lobby"
                className="bg-[#00FF66] hover:bg-[#00e65b] text-black font-bold uppercase tracking-widest px-5 py-2.5 text-sm rounded-sm inline-flex items-center gap-2"
              >
                {copy.primary.label} <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <div className="font-display text-4xl tracking-widest mb-4 text-[#FF3B30]">
              {status === "failed" ? "PAYMENT FAILED" : "CHECKING STATUS..."}
            </div>
            <p className="text-white/50 mb-6">
              {status === "failed"
                ? "Your entry was not confirmed. Please try again."
                : "It's taking longer than usual. Check My Entries in a moment."}
            </p>
            <button
              onClick={() => navigate("/lobby")}
              className="bg-[#00FF66] text-black font-bold uppercase tracking-widest px-5 py-2.5 text-sm rounded-sm"
            >
              Back to Lobby
            </button>
          </>
        )}
      </div>
    </Layout>
  );
}
